import { UseFormReturn } from "react-hook-form";
import { ReviewField, ReviewSection } from "@/components/shared/ReviewSection";
import { formatDateOnly } from "@/utils/dates";
import { useReviewFieldTransformers } from "../hooks/useReviewFieldTransformers";
import { formatAttendanceMonth } from "../../useATTNFormLogic";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

type CoreData = {
  countryList: Array<{ Value: string; Text: string }>;
  states: Array<{ Code: string; Name: string }>;
  raceList: Array<{ Value: string; Text: string }>;
  languagesList: Array<{ Value: string; Text: string }>;
  IEPorIFSPList?: Array<{ Value: string; Text: string }>;
  calWORKsList?: Array<{ Value: string; Text: string }>;
  educationList?: Array<{ Value: string; Text: string }>;
  reasonForServiceList?: Array<{ Value: string; Text: string }>;
  educationalProgramList?: Array<{ Value: string; Text: string }>;
  homelessDwellingTypeCodeList?: Array<{ Value: string; Text: string }>;
};

const sectionSubheaderStyle: React.CSSProperties = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#293038",
  paddingLeft: "12px",
  borderLeft: "3px solid #1e5a78",
};

const nestedCardStyle: React.CSSProperties = {
  border: "1px solid #e2e5e9",
  borderRadius: "8px",
  padding: "20px",
  backgroundColor: "#fafbfc",
};

const familyTitleStyle: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 600,
  color: "#1e5a78",
  marginBottom: "16px",
  paddingBottom: "10px",
  borderBottom: "2px solid #1e5a78",
};

type FamilyReviewPanelProps = {
  familyForm: UseFormReturn<any>;
  transformers: ReturnType<typeof useReviewFieldTransformers>;
  preschools: PreschoolSummary[];
};

function FamilyReviewPanel({
  familyForm,
  transformers,
  preschools,
}: FamilyReviewPanelProps) {
  return (
    <div className="row">
      <ReviewField
        label="Effective Start Date"
        value={
          familyForm.watch("EffectiveStartDate")
            ? formatDateOnly(familyForm.watch("EffectiveStartDate"))
            : undefined
        }
      />
      <ReviewField
        label="Local Family ID"
        value={familyForm.watch("LocalFamilyId")}
      />
      <ReviewField
        label="Preschool"
        value={
          preschools.find(
            (p) => p.PreschoolCDSCode === familyForm.watch("PreschoolCode")
          )?.PreschoolName
        }
      />
      <ReviewField label="Family Size" value={familyForm.watch("FamilySize")} />
      <ReviewField
        label="Family Monthly Income"
        value={familyForm.watch("FamilyIncome")}
      />
      <ReviewField
        label="CalWORKs Recipient"
        value={transformers.getCalWorksText(
          familyForm.watch("CALWorksRecipient")
        )}
      />

      <div className="col-12 mt-5 mb-3">
        <div style={sectionSubheaderStyle}>1st Head of Household</div>
      </div>
      <ReviewField
        label="First Name"
        value={familyForm.watch("HeadOfHousehold1FirstName")}
      />
      <ReviewField
        label="Middle Name"
        value={familyForm.watch("HeadOfHousehold1MiddleName")}
      />
      <ReviewField
        label="Last Name"
        value={familyForm.watch("HeadOfHousehold1LastName")}
      />
      <ReviewField
        label="Reasons For Needing Service"
        value={transformers.getServiceReasonsText("HeadOfHousehold1")}
      />
      <ReviewField
        label="Highest Education Level"
        value={transformers.getEducationText(
          familyForm.watch("HeadOfHousehold1HighestEducationLevel")
        )}
      />

      <div className="col-12 mt-5 mb-3">
        <div style={sectionSubheaderStyle}>2nd Head of Household</div>
      </div>
      <ReviewField
        label="First Name"
        value={familyForm.watch("HeadOfHousehold2FirstName")}
      />
      <ReviewField
        label="Middle Name"
        value={familyForm.watch("HeadOfHousehold2MiddleName")}
      />
      <ReviewField
        label="Last Name"
        value={familyForm.watch("HeadOfHousehold2LastName")}
      />
      <ReviewField
        label="Reasons For Needing Service"
        value={transformers.getServiceReasonsText("HeadOfHousehold2")}
      />
      <ReviewField
        label="Highest Education Level"
        value={transformers.getEducationText(
          familyForm.watch("HeadOfHousehold2HighestEducationLevel")
        )}
      />

      <div className="col-12 mt-5 mb-3">
        <div style={sectionSubheaderStyle}>Communication Preference</div>
      </div>
      <ReviewField
        label="Written Communication Preference"
        value={transformers.getLanguageText(
          familyForm.watch("HeadOfHouseholdWrittenCommunicationPreference")
        )}
      />
      <ReviewField
        label="Verbal Communication Preference"
        value={transformers.getLanguageText(
          familyForm.watch("HeadOfHouseholdVerbalCommunicationPreference")
        )}
      />

      <div className="col-12 mt-5 mb-3">
        <div style={sectionSubheaderStyle}>Home Address</div>
      </div>
      <ReviewField
        label="Residential Address 1"
        value={familyForm.watch("ResidentialAddress1")}
      />
      <ReviewField
        label="Residential Address 2"
        value={familyForm.watch("ResidentialAddress2")}
      />
      <ReviewField
        label="Residential City"
        value={familyForm.watch("ResidentialCity")}
      />
      <ReviewField
        label="Residential State"
        value={transformers.getStateText(familyForm.watch("ResidentialState"))}
      />
      <ReviewField
        label="Residential Zip"
        value={familyForm.watch("ResidentialZip")}
      />

      <div className="col-12 mt-5 mb-3">
        <div style={sectionSubheaderStyle}>Mailing Address</div>
      </div>
      <ReviewField
        label="Mailing Address 1"
        value={familyForm.watch("MailingAddress1")}
      />
      <ReviewField
        label="Mailing Address 2"
        value={familyForm.watch("MailingAddress2")}
      />
      <ReviewField
        label="Mailing City"
        value={familyForm.watch("MailingCity")}
      />
      <ReviewField
        label="Mailing State"
        value={transformers.getStateText(familyForm.watch("MailingState"))}
      />
      <ReviewField label="Mailing Zip" value={familyForm.watch("MailingZip")} />
    </div>
  );
}

type ClassroomReviewPanelProps = {
  classroomEnrollmentForm: UseFormReturn<any>;
};

function ClassroomReviewPanel({
  classroomEnrollmentForm,
}: ClassroomReviewPanelProps) {
  return (
    <div className="row">
      <ReviewField
        label="Classroom ID"
        value={classroomEnrollmentForm.watch("LocalClassroomId")}
      />
      <ReviewField
        label="Classroom Enrollment Start Date"
        value={
          classroomEnrollmentForm.watch("ClassroomEnrollmentStart")
            ? formatDateOnly(
                classroomEnrollmentForm.watch("ClassroomEnrollmentStart")
              )
            : undefined
        }
      />
      <ReviewField
        label="Classroom Enrollment End Date"
        value={
          classroomEnrollmentForm.watch("ClassroomEnrollmentEnd")
            ? formatDateOnly(
                classroomEnrollmentForm.watch("ClassroomEnrollmentEnd")
              )
            : undefined
        }
      />
    </div>
  );
}

type AttendanceReviewPanelProps = {
  attendanceForm: UseFormReturn<any>;
  preschools: PreschoolSummary[];
};

function AttendanceReviewPanel({
  attendanceForm,
  preschools,
}: AttendanceReviewPanelProps) {
  return (
    <div className="row">
      <ReviewField label="CSPPID" value={attendanceForm.watch("CSPPID")} />
      <ReviewField
        label="Local Child ID (CICN)"
        value={attendanceForm.watch("LocalChildId")}
      />
      <ReviewField
        label="Program Year"
        value={attendanceForm.watch("ProgramYear")}
      />
      <ReviewField
        label="Preschool"
        value={
          preschools.find(
            (p) => p.PreschoolCDSCode === attendanceForm.watch("PreschoolCode")
          )?.PreschoolName
        }
      />
      <ReviewField
        label="Attendance Month"
        value={formatAttendanceMonth(attendanceForm.watch("FirstOfMonth"))}
      />
      <ReviewField
        label="Days Expected"
        value={attendanceForm.watch("DaysExpected")}
      />
      <ReviewField
        label="Days Attended"
        value={attendanceForm.watch("DaysAttended")}
      />
      <ReviewField
        label="Days Absent (Excused)"
        value={attendanceForm.watch("DaysAbsentExcused")}
      />
      <ReviewField
        label="Days Absent (Unexcused)"
        value={attendanceForm.watch("DaysAbsentUnexcused")}
      />
      <ReviewField
        label="Days Absent (Suspension)"
        value={attendanceForm.watch("DaysAbsentSuspension")}
      />
      <ReviewField
        label="Days Attended (Suspension)"
        value={attendanceForm.watch("DaysAttendedSuspension")}
      />
    </div>
  );
}

type EducationProgramReviewPanelProps = {
  prgmForm: UseFormReturn<any>;
  preschools: PreschoolSummary[];
  coreData: CoreData;
  transformers: ReturnType<typeof useReviewFieldTransformers>;
};

function EducationProgramReviewPanel({
  prgmForm,
  preschools,
  coreData,
  transformers,
}: EducationProgramReviewPanelProps) {
  const eduProgramCode = prgmForm.watch("EduProgramCode");
  const homelessDwellingTypeCode = prgmForm.watch("HomelessDwellingTypeCode");

  return (
    <div className="row">
      <ReviewField label="CSPPID" value={prgmForm.watch("CSPPID")} />
      <ReviewField
        label="Local Child ID (CICN)"
        value={prgmForm.watch("LocalChildId")}
      />
      <ReviewField
        label="Program Year"
        value={prgmForm.watch("ProgramYear")}
      />
      <ReviewField
        label="School"
        value={
          preschools.find(
            (p) => p.PreschoolCDSCode === prgmForm.watch("PreschoolCode")
          )?.PreschoolName
        }
      />
      <ReviewField
        label="Education Program"
        value={
          coreData.educationalProgramList?.find(
            (p) => p.Value === eduProgramCode
          )?.Text || eduProgramCode
        }
      />
      <ReviewField
        label="Membership Start Date"
        value={
          prgmForm.watch("EduProgramMembershipStartDate")
            ? formatDateOnly(
                prgmForm.watch("EduProgramMembershipStartDate")
              )
            : undefined
        }
      />
      <ReviewField
        label="Membership End Date"
        value={
          prgmForm.watch("EduProgramMembershipEndDate")
            ? formatDateOnly(prgmForm.watch("EduProgramMembershipEndDate"))
            : undefined
        }
      />
      <ReviewField
        label="Homeless Dwelling Type"
        value={
          coreData.homelessDwellingTypeCodeList?.find(
            (h) => h.Value === homelessDwellingTypeCode
          )?.Text || homelessDwellingTypeCode
        }
      />
      <ReviewField
        label="Unaccompanied Youth Indicator"
        value={transformers.getYesNoText(
          prgmForm.watch("UnaccompaniedYouthIndicator")
        )}
      />
    </div>
  );
}

interface ReviewStepsProps {
  form: UseFormReturn<any>;
  familyForm: UseFormReturn<any>;
  familyForm2?: UseFormReturn<any>;
  showSecondFamily?: boolean;
  languageForm: UseFormReturn<any>;
  supplementalLanguageForm: UseFormReturn<any>;
  enrollmentForm: UseFormReturn<any>;
  classroomEnrollmentForms: UseFormReturn<any>[];
  activeClassroomCount: number;
  attendanceForms: UseFormReturn<any>[];
  activeAttendanceCount: number;
  prgmForms: UseFormReturn<any>[];
  activePrgmCount: number;
  preschools: PreschoolSummary[];
  coreData: CoreData;
  skipFamily: boolean;
  skipLanguage: boolean;
  skipSupplementalLanguage: boolean;
  skipEnrollment: boolean;
  skipClassroomEnrollment: boolean;
  skipAttendance: boolean;
  skipPrgm: boolean;
  onEditStep: (stepNumber: number) => void;
  canEditFamily?: boolean;
  canEditLanguage?: boolean;
  canEditSupplementalLanguage?: boolean;
  canEditEnrollment?: boolean;
  canEditClassroomEnrollment?: boolean;
  canEditAttendance?: boolean;
  canEditPrgm?: boolean;
}

export function ReviewSteps({
  form,
  familyForm,
  familyForm2,
  showSecondFamily = false,
  languageForm,
  supplementalLanguageForm,
  enrollmentForm,
  classroomEnrollmentForms,
  activeClassroomCount,
  attendanceForms,
  activeAttendanceCount,
  prgmForms,
  activePrgmCount,
  preschools,
  coreData,
  skipFamily,
  skipLanguage,
  skipSupplementalLanguage,
  skipEnrollment,
  skipClassroomEnrollment,
  skipAttendance,
  skipPrgm,
  onEditStep,
  canEditFamily = true,
  canEditLanguage = true,
  canEditSupplementalLanguage = true,
  canEditEnrollment = true,
  canEditClassroomEnrollment = true,
  canEditAttendance = true,
  canEditPrgm = true,
}: ReviewStepsProps) {
  const primaryTransformers = useReviewFieldTransformers(
    coreData,
    familyForm,
    form
  );

  const family2Transformers = useReviewFieldTransformers(
    coreData,
    familyForm2 ?? familyForm,
    form
  );

  const {
    getYesNoText,
    getCountryText,
    getStateText,
    getGenderText,
    getIEPIFSPText,
    getAllRacesText,
    getLanguageText,
    getMultiLanguagesText,
    getContractTypeText,
    getProgramTypeText,
    getProgramDurationText,
    getEligibilityStatusText,
    getExitReasonText,
  } = primaryTransformers;

  return (
    <div className="p-4">
      <div className="mb-4">
        <div className="d-flex align-items-center gap-2 mb-1">
          <i
            className="bi bi-clipboard-check"
            style={{ fontSize: "18px", color: "#1e5a78" }}
          ></i>
          <h3
            className="mb-0"
            style={{ fontWeight: 600, fontSize: "16px", lineHeight: 1 }}
          >
            Review & Submit
          </h3>
        </div>
        <p
          className="mb-0 text-muted"
          style={{ fontSize: "13px", paddingLeft: "26px" }}
        >
          Please review all information before submitting.
        </p>
      </div>

      <ReviewSection
        icon={<i className="bi bi-person"></i>}
        title="Basic Information"
        onEdit={() => onEditStep(2)}
      >
        <div className="row">
          <ReviewField label="CSPPID" value={form.watch("CSPPID")} />
          <ReviewField label="Program Year" value={form.watch("ProgramYear")} />
          <ReviewField
            label="Preschool"
            value={
              preschools.find(
                (p) => p.PreschoolCDSCode === form.watch("PreschoolCode")
              )?.PreschoolName
            }
          />
          <ReviewField
            label="Local Child ID (CICN)"
            value={form.watch("LocalChildId")}
          />
          <ReviewField label="SSID" value={form.watch("SSID")} />
          <ReviewField
            label="Effective Start Date"
            value={
              form.watch("EffectiveStartDate")
                ? formatDateOnly(form.watch("EffectiveStartDate"))
                : undefined
            }
          />
          <ReviewField
            label="First Name"
            value={form.watch("ChildFirstName")}
          />
          <ReviewField
            label="Middle Name"
            value={form.watch("ChildMiddleName")}
          />
          <ReviewField label="Last Name" value={form.watch("ChildLastName")} />
          <ReviewField label="Suffix" value={form.watch("ChildNameSuffix")} />
          <ReviewField
            label="Country of Birth"
            value={getCountryText(form.watch("ChildCountryOfBirth"))}
          />
          <ReviewField
            label="State of Birth"
            value={getStateText(form.watch("ChildStateOfBirth"))}
          />
          <ReviewField
            label="City of Birth"
            value={form.watch("ChildCityOfBirth")}
          />
          <ReviewField
            label="Date of Birth"
            value={
              form.watch("ChildDateOfBirth")
                ? formatDateOnly(form.watch("ChildDateOfBirth"))
                : undefined
            }
          />
          <ReviewField
            label="Gender"
            value={getGenderText(form.watch("ChildGender"))}
          />
          <ReviewField
            label="Hispanic"
            value={getYesNoText(form.watch("ChildEthnicity"))}
          />
          <ReviewField label="Race(s)" value={getAllRacesText()} />
          <ReviewField
            label="Active IEP or IFSP?"
            value={getIEPIFSPText(form.watch("IEPIFSPIndicator"))}
          />
        </div>
      </ReviewSection>

      {canEditFamily && (
        <ReviewSection
          icon={<i className="bi bi-people"></i>}
          title="Family Information"
          onEdit={() => onEditStep(3)}
          skipped={skipFamily}
        >
          {!skipFamily && (
            <div>
              <div
                style={
                  showSecondFamily && familyForm2
                    ? { ...nestedCardStyle, marginBottom: "20px" }
                    : undefined
                }
              >
                {showSecondFamily && familyForm2 && (
                  <div style={familyTitleStyle}>Family 1</div>
                )}
                <FamilyReviewPanel
                  familyForm={familyForm}
                  transformers={primaryTransformers}
                  preschools={preschools}
                />
              </div>

              {showSecondFamily && familyForm2 && (
                <div style={nestedCardStyle}>
                  <div style={familyTitleStyle}>Family 2</div>
                  <FamilyReviewPanel
                    familyForm={familyForm2}
                    transformers={family2Transformers}
                    preschools={preschools}
                  />
                </div>
              )}
            </div>
          )}
        </ReviewSection>
      )}

      {canEditLanguage && (
        <ReviewSection
          icon={<i className="bi bi-translate"></i>}
          title="Language"
          onEdit={() => onEditStep(4)}
          skipped={skipLanguage}
        >
          {!skipLanguage && (
            <div className="row">
              <ReviewField
                label="Instrument Opt Out"
                value={getYesNoText(languageForm.watch("InstrumentOptOut"))}
              />
              <ReviewField
                label="Instrument Date"
                value={
                  languageForm.watch("InstrumentDate")
                    ? formatDateOnly(languageForm.watch("InstrumentDate"))
                    : undefined
                }
              />
              <ReviewField
                label="DLL Instrument Designated"
                value={getYesNoText(
                  languageForm.watch("DLLInstrumentDesignated")
                )}
              />
              <ReviewField
                label="DLL Teacher Designated"
                value={getYesNoText(languageForm.watch("DLLTeacherDesignated"))}
              />
              <ReviewField
                label="DLL Teacher Designated Date"
                value={
                  languageForm.watch("DLLTeacherDesignatedDate")
                    ? formatDateOnly(
                        languageForm.watch("DLLTeacherDesignatedDate")
                      )
                    : undefined
                }
              />
              <ReviewField
                label="Language(s) Heard"
                value={getMultiLanguagesText([
                  languageForm.watch("InstrumentLanguagesHeard1"),
                  languageForm.watch("InstrumentLanguagesHeard2"),
                  languageForm.watch("InstrumentLanguagesHeard3"),
                ])}
              />
              <ReviewField
                label="Language(s) Understood"
                value={getMultiLanguagesText([
                  languageForm.watch("InstrumentLanguagesUnderstood1"),
                  languageForm.watch("InstrumentLanguagesUnderstood2"),
                  languageForm.watch("InstrumentLanguagesUnderstood3"),
                ])}
              />
              <ReviewField
                label="Language(s) Spoken"
                value={getMultiLanguagesText([
                  languageForm.watch("InstrumentLanguagesSpoken1"),
                  languageForm.watch("InstrumentLanguagesSpoken2"),
                  languageForm.watch("InstrumentLanguagesSpoken3"),
                ])}
              />
              <ReviewField
                label="Effective Start Date"
                value={
                  languageForm.watch("EffectiveStartDate")
                    ? formatDateOnly(languageForm.watch("EffectiveStartDate"))
                    : undefined
                }
              />
            </div>
          )}
        </ReviewSection>
      )}

      {canEditSupplementalLanguage && (
        <ReviewSection
          icon={<i className="bi bi-chat-dots"></i>}
          title="Supplemental Language"
          onEdit={() => onEditStep(5)}
          skipped={skipSupplementalLanguage}
        >
          {!skipSupplementalLanguage && (
            <div className="row">
              <ReviewField
                label="Interview Home Language(s)"
                value={getMultiLanguagesText([
                  supplementalLanguageForm.watch("InterviewHomeLanguage1"),
                  supplementalLanguageForm.watch("InterviewHomeLanguage2"),
                  supplementalLanguageForm.watch("InterviewHomeLanguage3"),
                ])}
              />
              <ReviewField
                label="Most Used Language"
                value={getLanguageText(
                  supplementalLanguageForm.watch("InterviewMostUsedLanguage")
                )}
              />
              <ReviewField
                label="Effective Start Date"
                value={
                  supplementalLanguageForm.watch("EffectiveStartDate")
                    ? formatDateOnly(
                        supplementalLanguageForm.watch("EffectiveStartDate")
                      )
                    : undefined
                }
              />
            </div>
          )}
        </ReviewSection>
      )}

      {canEditEnrollment && (
        <ReviewSection
          icon={<i className="bi bi-file-text"></i>}
          title="Enrollment"
          onEdit={() => onEditStep(6)}
          skipped={skipEnrollment}
        >
          {!skipEnrollment && (
            <div className="row">
              <ReviewField
                label="Contract Type"
                value={getContractTypeText(
                  enrollmentForm.watch("ContractType")
                )}
              />
              <ReviewField
                label="Program Type"
                value={getProgramTypeText(enrollmentForm.watch("ProgramType"))}
              />
              <ReviewField
                label="Program Duration"
                value={getProgramDurationText(
                  enrollmentForm.watch("ProgramDuration")
                )}
              />
              <ReviewField
                label="Eligibility Status"
                value={getEligibilityStatusText(
                  enrollmentForm.watch("EligibilityStatus")
                )}
              />
              <ReviewField
                label="Enrollment Start Datee"
                value={
                  enrollmentForm.watch("EnrollmentStartDate")
                    ? formatDateOnly(
                        enrollmentForm.watch("EnrollmentStartDate")
                      )
                    : undefined
                }
              />
              <ReviewField
                label="Enrollment End Date"
                value={
                  enrollmentForm.watch("EnrollmentEndDate")
                    ? formatDateOnly(enrollmentForm.watch("EnrollmentEndDate"))
                    : undefined
                }
              />
              <ReviewField
                label="Enrollment End Reason"
                value={getExitReasonText(
                  enrollmentForm.watch("EnrollmentEndReason")
                )}
              />
            </div>
          )}
        </ReviewSection>
      )}

      {canEditClassroomEnrollment && (
        <ReviewSection
          icon={<i className="bi bi-door-open"></i>}
          title="Classroom Enrollment"
          onEdit={() => onEditStep(7)}
          skipped={skipClassroomEnrollment}
        >
          {!skipClassroomEnrollment && (
            <div>
              {classroomEnrollmentForms
                .slice(0, activeClassroomCount)
                .map((f, i) => {
                  const isMulti = activeClassroomCount > 1;
                  return (
                    <div
                      key={i}
                      style={
                        isMulti
                          ? {
                              ...nestedCardStyle,
                              marginBottom:
                                i < activeClassroomCount - 1 ? "20px" : 0,
                            }
                          : undefined
                      }
                    >
                      {isMulti && (
                        <div style={familyTitleStyle}>
                          Classroom Enrollment {i + 1}
                        </div>
                      )}
                      <ClassroomReviewPanel classroomEnrollmentForm={f} />
                    </div>
                  );
                })}
            </div>
          )}
        </ReviewSection>
      )}

      {canEditAttendance && (
        <ReviewSection
          icon={<i className="bi bi-calendar-check"></i>}
          title="Attendance"
          onEdit={() => onEditStep(8)}
          skipped={skipAttendance}
        >
          {!skipAttendance && (
            <div>
              {attendanceForms
                .slice(0, activeAttendanceCount)
                .map((f, i) => {
                  const isMulti = activeAttendanceCount > 1;
                  return (
                    <div
                      key={i}
                      style={
                        isMulti
                          ? {
                              ...nestedCardStyle,
                              marginBottom:
                                i < activeAttendanceCount - 1 ? "20px" : 0,
                            }
                          : undefined
                      }
                    >
                      {isMulti && (
                        <div style={familyTitleStyle}>Attendance {i + 1}</div>
                      )}
                      <AttendanceReviewPanel
                        attendanceForm={f}
                        preschools={preschools}
                      />
                    </div>
                  );
                })}
            </div>
          )}
        </ReviewSection>
      )}

      {canEditPrgm && (
        <ReviewSection
          icon={<i className="bi bi-briefcase"></i>}
          title="Education Programs"
          onEdit={() => onEditStep(9)}
          skipped={skipPrgm}
        >
          {!skipPrgm && (
            <div>
              {prgmForms
                .slice(0, activePrgmCount)
                .map((f, i) => {
                  const isMulti = activePrgmCount > 1;
                  return (
                    <div
                      key={i}
                      style={
                        isMulti
                          ? {
                              ...nestedCardStyle,
                              marginBottom:
                                i < activePrgmCount - 1 ? "20px" : 0,
                            }
                          : undefined
                      }
                    >
                      {isMulti && (
                        <div style={familyTitleStyle}>
                          Education Program {i + 1}
                        </div>
                      )}
                      <EducationProgramReviewPanel
                        prgmForm={f}
                        preschools={preschools}
                        coreData={coreData}
                        transformers={primaryTransformers}
                      />
                    </div>
                  );
                })}
            </div>
          )}
        </ReviewSection>
      )}
    </div>
  );
}
